import { motion } from 'framer-motion';
import { CalendarRange } from 'lucide-react';
import { SOVEREIGN } from '../../../utils/epcProjectVisual';
import { durationParts, mdy } from '../format';

type Props = {
  /** 'YYYY-MM-DD' or '' */
  start: string;
  end: string;
  commissioning: string;
  accent: string;
};

function pctBetween(t: number, a: number, b: number): number {
  if (b <= a) return 0;
  return ((t - a) / (b - a)) * 100;
}

/** First-of-month Dates strictly inside (a, b). Thinned so at most ~12 ticks render. */
function monthTicks(a: Date, b: Date): Date[] {
  const ticks: Date[] = [];
  const d = new Date(Date.UTC(a.getUTCFullYear(), a.getUTCMonth() + 1, 1));
  while (d.getTime() < b.getTime()) {
    ticks.push(new Date(d.getTime()));
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  const step = Math.max(1, Math.ceil(ticks.length / 12));
  return ticks.filter((_, i) => i % step === 0);
}

export default function TimelineGraph({ start, end, commissioning, accent: _accent }: Props) {
  const dur = durationParts(start, end);
  if (!start || !end || !dur) {
    return (
      <div
        className="relative flex flex-col items-center justify-center gap-1.5 rounded-2xl px-4 py-8 text-center"
        style={{ border: '1px dashed rgba(11,59,46,0.25)', background: 'rgba(250,247,238,0.6)' }}
      >
        <CalendarRange className="h-5 w-5" style={{ color: SOVEREIGN.kpiCaption }} />
        <p className="text-sm font-medium" style={{ color: SOVEREIGN.kpiCaption }}>
          Pick a start and planned end date to see the project timeline
        </p>
      </div>
    );
  }

  const s = new Date(start);
  const e = new Date(end);
  const c = commissioning ? new Date(commissioning) : null;
  const today = new Date(new Date().toISOString().slice(0, 10));

  // Window covers span plus commissioning overshoot, padded 5% each side.
  const winStart = s.getTime();
  const winEnd = Math.max(e.getTime(), c ? c.getTime() : 0);
  const spanMs = Math.max(winEnd - winStart, 86_400_000);
  const pad = spanMs * 0.05;
  const a = winStart - pad;
  const b = winEnd + pad;

  const leftPct = pctBetween(winStart, a, b);
  const rightPct = pctBetween(e.getTime(), a, b);
  const commPct = c && !Number.isNaN(c.getTime()) ? pctBetween(c.getTime(), a, b) : null;
  const todayPct =
    today.getTime() >= a && today.getTime() <= b ? pctBetween(today.getTime(), a, b) : null;
  const ticks = monthTicks(new Date(a), new Date(b));

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-4"
      style={{ border: '1px solid rgba(11,59,46,0.35)', background: 'rgba(250,247,238,0.96)' }}
    >
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: SOVEREIGN.title }}>
          Project timeline
        </p>
        <span
          className="rounded-md px-2 py-0.5 font-mono text-[11px] font-bold tabular-nums"
          style={{ background: 'rgba(201,169,97,0.14)', color: '#8A5A08', border: '1px solid rgba(201,169,97,0.4)' }}
        >
          {dur.days} days · ~{dur.months} months
        </span>
      </div>

      <div className="relative mt-6 h-11">
        {/* month ticks */}
        {ticks.map((t) => {
          const p = pctBetween(t.getTime(), a, b);
          return (
            <span key={t.toISOString()} className="absolute top-0 h-11" style={{ left: `${p}%` }}>
              <span className="absolute top-1 block h-6 w-px" style={{ background: 'rgba(11,59,46,0.15)' }} />
              <span
                className="absolute top-8 -translate-x-1/2 whitespace-nowrap font-mono text-[9px]"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                {t.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' })}
              </span>
            </span>
          );
        })}

        {/* track */}
        <div
          className="absolute left-0 right-0 top-2 h-3 rounded-full"
          style={{ background: SOVEREIGN.barTrack, border: `1px solid ${SOVEREIGN.barTrackBorder}` }}
        />

        {/* span bar */}
        <motion.div
          className="absolute top-2 h-3 rounded-full"
          initial={{ opacity: 0, scaleX: 0.6 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          style={{
            left: `${leftPct}%`,
            width: `${Math.max(rightPct - leftPct, 1)}%`,
            background: SOVEREIGN.barFill,
            transformOrigin: 'left center',
          }}
        />

        {/* commissioning diamond */}
        {commPct != null ? (
          <span
            className="absolute top-[7px] block h-3.5 w-3.5 -translate-x-1/2 rotate-45"
            title={`Commissioning ${mdy(commissioning)}`}
            style={{ left: `${commPct}%`, background: '#C9A961', border: '1.5px solid #8A5A08', boxShadow: '0 1px 3px rgba(42,31,23,0.35)' }}
          />
        ) : null}

        {/* today marker */}
        {todayPct != null ? (
          <span className="absolute -top-4 h-11" style={{ left: `${todayPct}%` }}>
            <span className="absolute top-2 block h-9 w-px" style={{ background: SOVEREIGN.title }} />
            <span
              className="absolute top-0 -translate-x-1/2 rounded px-1 text-[8px] font-bold uppercase tracking-wider"
              style={{ color: SOVEREIGN.title }}
            >
              Today
            </span>
          </span>
        ) : null}
      </div>

      <div className="mt-2 flex items-center justify-between font-mono text-[11px] font-semibold tabular-nums" style={{ color: SOVEREIGN.ink }}>
        <span title="Start date">▸ {mdy(start)}</span>
        {commissioning ? (
          <span title="Commissioning" style={{ color: '#8A5A08' }}>◆ {mdy(commissioning)}</span>
        ) : null}
        <span title="Planned end">{mdy(end)} ◂</span>
      </div>
    </div>
  );
}
